/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */

import * as cc from './cipher.consts';
import { BYOBStreamReader, bytesToNum, getRandom } from './utils';

import {
   Ciphers,
   Encipher,
   Decipher,
   EncipherV8,
   DecipherV678,
   CipherState,
   type CipherDataInfo,
   type ReadOpts,
} from './ciphers-current';

import { DecipherV1, DecipherV4, DecipherV5 } from './deciphers-old';
import { type KeyProvider, type PWDProvider } from './keys';

export { Ciphers, Encipher, Decipher, CipherState };

export type { PWDProvider, CipherDataInfo, ReadOpts };

export function getLatestEncipher(
   clearStream: ReadableStream<Uint8Array>,
   keyProvider: KeyProvider,
   alg: cc.CipherAlgs,
   lp: number,
   lpEnd: number,
   ic: number = 0,
   readOpts?: ReadOpts,
): Encipher {
   const slt = getRandom(cc.SLT_BYTES);
   keyProvider.setCipherDataInfo({
      ver: cc.CURRENT_VERSION,
      alg,
      lp,
      lpEnd,
      slt,
      ic,
   });

   const reader = new BYOBStreamReader(clearStream);
   return new EncipherV8(keyProvider, reader, readOpts);
}

// Return appropriate version of Decipher. pwdProvider can be undefined when
// only CipherDataInfo is needed
export async function getStreamDecipher(
   cipherStream: ReadableStream<Uint8Array>,
   keyProvider: KeyProvider,
): Promise<Decipher> {
   let decipher: Decipher;
   const reader = new BYOBStreamReader(cipherStream);

   const [header, done] = await reader.readFill(new ArrayBuffer(cc.HEADER_BYTES_6P));

   if (header.byteLength !== cc.HEADER_BYTES_6P || done) {
      reader.cleanup();
      throw new Error(`Invalid cipher header length: ${header.byteLength}`);
   }

   // This is rather ugly, but the original CiphersV1 encoding stupidly had the
   // version in the middle of the encoding. So detect old version by the first 2 bytes
   // after MAC being < 4 (since encoded started with ALG and v1 max ALG was 3 and beyond v1
   // version is >=4). WARNING: Breaks if ALG_BYTES or VER_BYTES sizes changes.
   const verOrAlg = bytesToNum(header.subarray(cc.MAC_BYTES, cc.MAC_BYTES + cc.VER_BYTES));

   if (verOrAlg === cc.VERSION6 || verOrAlg === cc.VERSION7 || verOrAlg === cc.VERSION8) {
      decipher = new DecipherV678(keyProvider, reader, header);
   } else {
      if (verOrAlg === cc.VERSION5) {
         decipher = new DecipherV5(keyProvider, reader, header);
      } else if (verOrAlg === cc.VERSION4) {
         decipher = new DecipherV4(keyProvider, reader, header);
      } else if (verOrAlg < cc.V1_BELOW && verOrAlg > 0) {
         decipher = new DecipherV1(keyProvider, reader, header);
      } else {
         throw new Error(`Invalid version: ${verOrAlg}`);
      }
   }
   return decipher;
}
